import html2canvas from "html2canvas";

const LOGO_CANDIDATES = [
  `${process.env.PUBLIC_URL || ""}/logo.png`,
  `${process.env.PUBLIC_URL || ""}/OMW_logo.png`,
  "/logo.png",
  "/OMW_logo.png",
];

let logoDataUrlCache: string | null = null;
let logoDataUrlPromise: Promise<string> | null = null;

const toAbsoluteUrl = (path: string): string => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return new URL(path, window.location.origin).href;
};

const imageElementToDataUrl = (img: HTMLImageElement): string | null => {
  if (!img.naturalWidth || !img.naturalHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/png");
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read logo blob"));
    reader.readAsDataURL(blob);
  });

const fetchLogoAsDataUrl = async (path: string): Promise<string | null> => {
  const url = toAbsoluteUrl(path);
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return blobToDataUrl(await res.blob());
  } catch {
    return null;
  }
};

const loadImageAsDataUrl = (path: string): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(imageElementToDataUrl(img));
    img.onerror = () => resolve(null);
    img.src = toAbsoluteUrl(path);
  });

const tryLogoFromDom = (): string | null => {
  const selectors = [
    "img.certificate__logo",
    ".certificate__header img",
    'img[src*="logo"]',
    'img[src*="OMW"]',
  ];
  for (const selector of selectors) {
    const img = document.querySelector(selector) as HTMLImageElement | null;
    if (img?.complete && img.naturalWidth > 0) {
      const dataUrl = imageElementToDataUrl(img);
      if (dataUrl) return dataUrl;
    }
  }
  return null;
};

export const ensureLogoDataUrl = async (): Promise<string> => {
  if (logoDataUrlCache) return logoDataUrlCache;
  if (logoDataUrlPromise) return logoDataUrlPromise;

  logoDataUrlPromise = (async () => {
    const fromDom = tryLogoFromDom();
    if (fromDom) {
      logoDataUrlCache = fromDom;
      return fromDom;
    }

    for (const path of LOGO_CANDIDATES) {
      const fromFetch = await fetchLogoAsDataUrl(path);
      if (fromFetch) {
        logoDataUrlCache = fromFetch;
        return fromFetch;
      }
      const fromImg = await loadImageAsDataUrl(path);
      if (fromImg) {
        logoDataUrlCache = fromImg;
        return fromImg;
      }
    }

    return "";
  })();

  try {
    return await logoDataUrlPromise;
  } finally {
    logoDataUrlPromise = null;
  }
};

export const preloadCertificateAssets = (): Promise<string> =>
  ensureLogoDataUrl();

const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const getCertificateElement = (node: HTMLElement): HTMLElement => {
  const certificate = node.querySelector(".certificate") as HTMLElement | null;
  return certificate || node;
};

const readCertColor = (el: HTMLElement): string => {
  const fromVar = getComputedStyle(el).getPropertyValue("--cert-color").trim();
  if (fromVar) return fromVar;
  const inline = el.style.getPropertyValue("--cert-color").trim();
  return inline || "#3FA9FF";
};

const isSquareCertificate = (el: HTMLElement) =>
  el.classList.contains("certificate--square");

/** Fixed export size — PDF layout stays identical at any browser zoom or window width. */
export const SQUARE_EXPORT_CSS_PX = 450;

/** Square export uses a fixed reference size, not the on-screen preview dimensions. */
const readElementSize = (el: HTMLElement) => {
  if (isSquareCertificate(el)) {
    return { width: SQUARE_EXPORT_CSS_PX, height: SQUARE_EXPORT_CSS_PX };
  }

  const rect = el.getBoundingClientRect();
  const width = Math.max(Math.ceil(rect.width), 50);
  const height = Math.max(Math.ceil(rect.height), 50);
  return { width, height };
};

const enforceSquareCanvas = (
  canvas: HTMLCanvasElement,
  cssWidth: number,
  scale: number,
): HTMLCanvasElement => {
  const targetPx = Math.round(cssWidth * scale);
  if (canvas.width === targetPx && canvas.height === targetPx) {
    return canvas;
  }

  const square = document.createElement("canvas");
  square.width = targetPx;
  square.height = targetPx;
  const ctx = square.getContext("2d");
  if (!ctx) return canvas;

  ctx.fillStyle = "#0a1830";
  ctx.fillRect(0, 0, targetPx, targetPx);

  const cropSide = Math.min(canvas.width, canvas.height);
  const offsetX = Math.max(0, Math.floor((canvas.width - cropSide) / 2));
  const offsetY = Math.max(0, Math.floor((canvas.height - cropSide) / 2));
  ctx.drawImage(
    canvas,
    offsetX,
    offsetY,
    cropSide,
    cropSide,
    0,
    0,
    targetPx,
    targetPx,
  );
  return square;
};

const waitForImages = (root: HTMLElement): Promise<void> =>
  Promise.all(
    Array.from(root.querySelectorAll("img")).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          window.setTimeout(done, 2000);
        }),
    ),
  ).then(() => undefined);

const shouldIgnoreForCapture = (element: Element): boolean => {
  if (element.tagName?.toLowerCase() === "svg") return true;
  if (!(element instanceof HTMLElement)) return false;
  return (
    element.classList.contains("certificate__icon-wrap") ||
    element.classList.contains("certificate__corner")
  );
};

const applyLogoToImages = (
  root: HTMLElement,
  logoDataUrl: string,
  logoHeightPx?: number,
) => {
  const logos = root.querySelectorAll("img.certificate__logo, .certificate__header img");

  if (!logoDataUrl.startsWith("data:image")) {
    logos.forEach((img) => {
      (img as HTMLElement).style.display = "none";
    });
    return;
  }

  logos.forEach((img) => {
    const image = img as HTMLImageElement;
    image.removeAttribute("crossorigin");
    image.src = logoDataUrl;
    if (logoHeightPx != null) {
      image.style.height = `${logoHeightPx}px`;
    }
    image.style.width = "auto";
    image.style.display = "block";
  });
};

const setElStyle = (el: HTMLElement, styles: Record<string, string>) => {
  Object.assign(el.style, styles);
};

const scalePxValue = (value: string, scale: number): string => {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return `${Math.round(n * scale)}px`;
};

const bumpPx = (value: string, extra: number): string => {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return `${Math.round(n + extra)}px`;
};

const scaledLineHeight = (
  computed: CSSStyleDeclaration,
  scale: number,
  minRatio = 1.45,
): string => {
  const lh = computed.lineHeight.trim();
  if (lh.endsWith("px")) {
    return bumpPx(scalePxValue(lh, scale), 2);
  }
  const ratio = Number.parseFloat(lh);
  if (Number.isFinite(ratio) && ratio > 0) {
    return String(Math.max(ratio, minRatio));
  }
  return String(minRatio);
};

const readScaledPx = (
  sourceCert: HTMLElement,
  selector: string,
  prop: "fontSize" | "marginTop" | "marginBottom" | "paddingTop" | "paddingRight" | "paddingBottom" | "paddingLeft" | "gap" | "height" | "width" | "maxWidth",
  scale: number,
  fallback: string,
): string => {
  const el = sourceCert.querySelector(selector) as HTMLElement | null;
  if (!el) return fallback;
  const raw = getComputedStyle(el)[prop];
  if (typeof raw !== "string" || !raw) return fallback;
  if (raw.endsWith("px")) return scalePxValue(raw, scale);
  return raw;
};

/** Explicit block/flex layout for PDF — scale fonts/margins from preview only (no flex copy). */
const applySquareExportLayout = (
  sourceCert: HTMLElement,
  rootCert: HTMLElement,
  exportPx: number,
  certColor: string,
) => {
  const sourceW = Math.max(sourceCert.getBoundingClientRect().width, 1);
  const scale = exportPx / sourceW;
  const titleLineGap = bumpPx(
    readScaledPx(sourceCert, ".certificate__divider", "marginTop", scale, "6px"),
    8,
  );

  const paintText = (selector: string, extra: Record<string, string> = {}) => {
    const sourceEls = sourceCert.querySelectorAll(selector);
    rootCert.querySelectorAll(selector).forEach((el, index) => {
      const src = sourceEls[index];
      if (!(src instanceof HTMLElement) || !(el instanceof HTMLElement)) return;
      const computed = getComputedStyle(src);
      setElStyle(el, {
        position: "static",
        display: "block",
        color: computed.color,
        fontSize: scalePxValue(computed.fontSize, scale),
        fontWeight: computed.fontWeight,
        fontFamily: computed.fontFamily,
        lineHeight: scaledLineHeight(computed, scale),
        letterSpacing: computed.letterSpacing,
        textAlign: "center",
        textShadow: "none",
        marginLeft: "0",
        marginRight: "0",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        ...extra,
      });
    });
  };

  const rootInner = rootCert.querySelector(".certificate__inner") as HTMLElement | null;
  const sourceInner = sourceCert.querySelector(".certificate__inner") as HTMLElement | null;
  if (rootInner && sourceInner) {
    const ic = getComputedStyle(sourceInner);
    setElStyle(rootInner, {
      position: "absolute",
      top: "14px",
      right: "14px",
      bottom: "14px",
      left: "14px",
      margin: "0",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "stretch",
      textAlign: "center",
      boxSizing: "border-box",
      overflow: "visible",
      gap: "0",
      paddingTop: bumpPx(scalePxValue(ic.paddingTop, scale), 16),
      paddingRight: bumpPx(scalePxValue(ic.paddingRight, scale), 8),
      paddingBottom: bumpPx(scalePxValue(ic.paddingBottom, scale), 16),
      paddingLeft: bumpPx(scalePxValue(ic.paddingLeft, scale), 8),
    });
  }

  rootCert.querySelectorAll(".certificate__header").forEach((el) => {
    setElStyle(el as HTMLElement, {
      position: "static",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%",
      flexShrink: "0",
      marginTop: "0",
      marginBottom: bumpPx(
        readScaledPx(sourceCert, ".certificate__header", "marginBottom", scale, "36px"),
        14,
      ),
    });
  });

  rootCert.querySelectorAll("img.certificate__logo").forEach((el, index) => {
    const src = sourceCert.querySelectorAll("img.certificate__logo")[index];
    if (!(el instanceof HTMLImageElement)) return;
    const logoHeight = src instanceof HTMLImageElement
      ? scalePxValue(getComputedStyle(src).height, scale)
      : `${Math.round(34 * scale)}px`;
    setElStyle(el, {
      display: "block",
      height: logoHeight,
      width: "auto",
      margin: "0 auto",
    });
  });

  paintText(".certificate__brand-name", {
    display: "block",
    marginTop: readScaledPx(sourceCert, ".certificate__brand-name", "marginTop", scale, "4px"),
    marginBottom: "0",
    width: "auto",
  });

  rootCert.querySelectorAll(".certificate__title-block").forEach((el) => {
    setElStyle(el as HTMLElement, {
      position: "static",
      display: "block",
      width: "100%",
      flexShrink: "0",
      margin: "0",
    });
  });

  paintText(".certificate__title", {
    marginTop: "0",
    marginBottom: "0",
    paddingBottom: titleLineGap,
    lineHeight: (() => {
      const src = sourceCert.querySelector(".certificate__title");
      return src instanceof HTMLElement
        ? scaledLineHeight(getComputedStyle(src), scale, 1.35)
        : "1.35";
    })(),
  });

  rootCert.querySelectorAll(".certificate__divider").forEach((el) => {
    setElStyle(el as HTMLElement, {
      position: "static",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      flexShrink: "0",
      gap: bumpPx(readScaledPx(sourceCert, ".certificate__divider", "gap", scale, "6px"), 2),
      marginTop: "0",
      marginBottom: "0",
    });
  });

  rootCert.querySelectorAll(".certificate__divider span").forEach((el) => {
    setElStyle(el as HTMLElement, {
      position: "static",
      display: "block",
      flex: "1 1 0",
      order: "1",
      minWidth: "24px",
      height: "1.5px",
      background: certColor,
      opacity: "0.7",
    });
  });

  rootCert.querySelectorAll(".certificate__subtitle").forEach((el, index) => {
    const src = sourceCert.querySelectorAll(".certificate__subtitle")[index];
    if (!(el instanceof HTMLElement) || !(src instanceof HTMLElement)) return;
    const computed = getComputedStyle(src);
    setElStyle(el, {
      position: "static",
      display: "block",
      order: "2",
      flex: "1 1 100%",
      width: "100%",
      maxWidth: "100%",
      minWidth: "0",
      margin: "0",
      marginTop: bumpPx(readScaledPx(sourceCert, ".certificate__subtitle", "marginTop", scale, "0px"), 8),
      padding: "4px 6px 0",
      boxSizing: "border-box",
      color: computed.color,
      fontSize: scalePxValue(computed.fontSize, scale),
      fontWeight: computed.fontWeight,
      fontFamily: computed.fontFamily,
      lineHeight: scaledLineHeight(computed, scale, 1.55),
      letterSpacing: computed.letterSpacing,
      textAlign: "center",
      textShadow: "none",
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "break-word",
    });
  });

  rootCert.querySelectorAll(".certificate__body").forEach((el) => {
    setElStyle(el as HTMLElement, {
      position: "static",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%",
      maxWidth: "100%",
      flexShrink: "0",
      marginTop: bumpPx(
        readScaledPx(sourceCert, ".certificate__body", "marginTop", scale, "40px"),
        14,
      ),
      marginBottom: "0",
    });
  });

  paintText(".certificate__intro", {
    marginTop: "0",
    marginBottom: bumpPx(
      readScaledPx(sourceCert, ".certificate__intro", "marginBottom", scale, "14px"),
      8,
    ),
    flexShrink: "0",
  });

  paintText(".certificate__name", {
    marginTop: "0",
    marginBottom: "0",
    flexShrink: "0",
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  });

  rootCert.querySelectorAll(".certificate__name-underline").forEach((el, index) => {
    const src = sourceCert.querySelectorAll(".certificate__name-underline")[index];
    if (!(el instanceof HTMLElement)) return;
    const computed = src instanceof HTMLElement ? getComputedStyle(src) : null;
    setElStyle(el, {
      position: "static",
      display: "block",
      flexShrink: "0",
      width: computed ? scalePxValue(computed.width, scale) : "165px",
      maxWidth: computed?.maxWidth || "75%",
      height: "1px",
      minHeight: "1px",
      marginTop: computed
        ? bumpPx(scalePxValue(computed.marginTop, scale), 6)
        : "14px",
      marginLeft: "auto",
      marginRight: "auto",
      marginBottom: "0",
      background: certColor,
      backgroundImage: "none",
      opacity: "0.85",
    });
  });
};

/** Square PDF export — fixed 450px shell; typography/spacing scaled from preview. */
const applyFixedSquareExportStyles = (
  sourceCert: HTMLElement,
  rootCert: HTMLElement,
  logoDataUrl: string,
  certColor: string,
) => {
  const px = SQUARE_EXPORT_CSS_PX;

  rootCert.classList.add("certificate--export");

  setElStyle(rootCert, {
    position: "relative",
    background: "#0a1830",
    borderRadius: getComputedStyle(sourceCert).borderRadius || "14px",
    boxShadow: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    containerType: "inline-size",
    width: `${px}px`,
    height: `${px}px`,
    minWidth: `${px}px`,
    minHeight: `${px}px`,
    maxWidth: `${px}px`,
    maxHeight: `${px}px`,
    padding: "0",
  });
  rootCert.style.setProperty("--cert-color", certColor);

  const rootInner = rootCert.querySelector(".certificate__inner") as HTMLElement | null;
  if (rootInner) {
    setElStyle(rootInner, {
      border: `2px solid ${certColor}`,
      borderRadius: "8px",
      background: "transparent",
      boxSizing: "border-box",
      overflow: "visible",
    });
  }

  rootCert.querySelectorAll(".certificate__corner").forEach((el) => {
    (el as HTMLElement).style.display = "none";
  });

  rootCert.querySelectorAll(".certificate__icon-wrap").forEach((el) => {
    (el as HTMLElement).style.display = "none";
  });

  applySquareExportLayout(sourceCert, rootCert, px, certColor);
  applyLogoToImages(rootCert, logoDataUrl);
};

/** Style an off-screen clone for html2canvas (solid colors only — avoids createPattern errors). */
const applyCaptureStyles = (
  sourceCert: HTMLElement,
  rootCert: HTMLElement,
  logoDataUrl: string,
) => {
  const certColor = readCertColor(sourceCert);

  if (isSquareCertificate(sourceCert)) {
    applyFixedSquareExportStyles(sourceCert, rootCert, logoDataUrl, certColor);
    return;
  }

  const sourceInner = sourceCert.querySelector(".certificate__inner") as HTMLElement | null;
  const rootInner = rootCert.querySelector(".certificate__inner") as HTMLElement | null;
  const { width, height } = readElementSize(sourceCert);

  rootCert.style.setProperty("--cert-color", certColor);
  rootCert.style.position = "relative";
  rootCert.style.background = "#0a1830";
  rootCert.style.borderRadius = getComputedStyle(sourceCert).borderRadius;
  rootCert.style.boxShadow = "none";
  rootCert.style.overflow = "hidden";
  rootCert.style.display = "flex";
  rootCert.style.flexDirection = "column";
  rootCert.style.boxSizing = "border-box";
  rootCert.style.width = `${width}px`;
  rootCert.style.height = `${height}px`;
  rootCert.style.minWidth = `${width}px`;
  rootCert.style.minHeight = `${height}px`;
  rootCert.style.maxWidth = `${width}px`;
  rootCert.style.maxHeight = `${height}px`;

  if (rootInner && sourceInner) {
    const innerComputed = getComputedStyle(sourceInner);
    rootInner.style.border = `2px solid ${certColor}`;
    rootInner.style.borderRadius = innerComputed.borderRadius;
    rootInner.style.padding = innerComputed.padding;
    rootInner.style.background = "transparent";
    rootInner.style.boxSizing = "border-box";
    rootInner.style.display = innerComputed.display;
    rootInner.style.flexDirection = innerComputed.flexDirection;
    rootInner.style.justifyContent = innerComputed.justifyContent;
    rootInner.style.alignItems = innerComputed.alignItems;
    rootInner.style.textAlign = innerComputed.textAlign;
    rootInner.style.overflow = "hidden";
    rootInner.style.minHeight = "0";
    rootInner.style.position = "relative";
    rootInner.style.margin = innerComputed.margin;
    rootInner.style.flex = "1 1 0";
    rootInner.style.width = "100%";
    rootInner.style.height = "100%";
  }

  const copyTextStyle = (selector: string) => {
    const sourceEls = sourceCert.querySelectorAll(selector);
    const rootEls = rootCert.querySelectorAll(selector);
    sourceEls.forEach((src, index) => {
      const dest = rootEls[index];
      if (!(src instanceof HTMLElement) || !(dest instanceof HTMLElement)) return;
      const computed = getComputedStyle(src);
      dest.style.color = computed.color;
      dest.style.fontSize = computed.fontSize;
      dest.style.fontWeight = computed.fontWeight;
      dest.style.fontFamily = computed.fontFamily;
      dest.style.letterSpacing = computed.letterSpacing;
      dest.style.lineHeight = computed.lineHeight;
      dest.style.textAlign = computed.textAlign;
      dest.style.textShadow = "none";
      dest.style.margin = computed.margin;
    });
  };

  copyTextStyle(".certificate__title");
  copyTextStyle(".certificate__subtitle");
  copyTextStyle(".certificate__intro");
  copyTextStyle(".certificate__name");
  copyTextStyle(".certificate__brand-name");

  rootCert.querySelectorAll(".certificate__divider span").forEach((el) => {
    const span = el as HTMLElement;
    span.style.background = certColor;
    span.style.opacity = "0.7";
  });

  rootCert.querySelectorAll(".certificate__name-underline").forEach((el) => {
    const line = el as HTMLElement;
    line.style.background = certColor;
    line.style.opacity = "0.85";
  });

  rootCert.querySelectorAll(".certificate__corner").forEach((el) => {
    (el as HTMLElement).style.display = "none";
  });

  rootCert.querySelectorAll(".certificate__icon-wrap").forEach((el) => {
    (el as HTMLElement).style.display = "none";
  });

  applyLogoToImages(rootCert, logoDataUrl);
};

const createOffScreenClone = (sourceCert: HTMLElement): HTMLElement => {
  const mount = document.createElement("div");
  mount.setAttribute("aria-hidden", "true");
  mount.style.cssText =
    "position:fixed;left:-10000px;top:0;pointer-events:none;z-index:-1;overflow:visible;";

  const clone = sourceCert.cloneNode(true) as HTMLElement;
  mount.appendChild(clone);
  document.body.appendChild(mount);
  return mount;
};

const removeOffScreenClone = (mount: HTMLElement) => {
  if (mount.parentNode) {
    mount.parentNode.removeChild(mount);
  }
};

export const captureCertificateElement = async (
  node: HTMLElement,
): Promise<HTMLCanvasElement> => {
  const logoDataUrl = await ensureLogoDataUrl();

  const sourceCert = getCertificateElement(node);
  node.scrollIntoView({ block: "center", inline: "nearest" });
  await waitForPaint();
  await new Promise((resolve) => setTimeout(resolve, 300));

  const { width, height } = readElementSize(sourceCert);
  if (width < 50 || height < 50) {
    throw new Error(
      "Certificate is not visible. Scroll the certificate into view and try again.",
    );
  }

  const captureScale = 2;

  const mount = createOffScreenClone(sourceCert);
  const clone = getCertificateElement(mount);

  try {
    applyCaptureStyles(sourceCert, clone, logoDataUrl);
    await waitForImages(clone);
    await waitForPaint();

    const frame = document.createElement("div");
    frame.style.cssText = [
      "position:fixed",
      "left:-10000px",
      "top:0",
      "overflow:visible",
      `width:${width}px`,
      `height:${height}px`,
    ].join(";");
    mount.insertBefore(frame, clone);
    frame.appendChild(clone);

    const canvas = await html2canvas(frame, {
      backgroundColor: "#0a1830",
      scale: captureScale,
      width,
      height,
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 20000,
      ignoreElements: shouldIgnoreForCapture,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Certificate capture produced an empty image.");
    }

    return isSquareCertificate(sourceCert)
      ? enforceSquareCanvas(canvas, width, captureScale)
      : canvas;
  } finally {
    removeOffScreenClone(mount);
  }
};
